import React from 'react';

import { ApprovalStatus, CampaignType, EarningRuleRewardType, EarningRuleType } from 'config/CustomEnums';
import TypeSelector from 'components/campaignStreamline/TypeSelector';
import ReactHookFormTitleWithRadio from 'components/reactHookFormComponents/ReactHookFormTitleWithRadio';
import CouponDropdownWithButtonAndPrompt from 'components/earning/CouponDropdownWithButtonAndPrompt';

import CouponCampaignType from '../../../assets/images/campaignStreamline/coupon_campaign_type.png';
import EarningCampaignType from '../../../assets/images/campaignStreamline/earning_campaign_type.png';

import EarningRuleGerneralPurchase from 'assets/images/earning/general_purchase.png';
import EarningRuleQRCode from 'assets/images/earning/qrcode_scanning.png';
import EarningRuleGPS from 'assets/images/earning/gps_check_in.png';
import EarningRuleNewMember from 'assets/images/earning/new_member.png';
import EarningRuleReferral from 'assets/images/earning/referral.png';
import EarningRuleBirthday from 'assets/images/earning/birthday.png';
import EarningRuleLevelUpgrade from 'assets/images/earning/levelUpgrade.png';
import EarningRuleWebhook from 'assets/images/earning/webhook.png';
import { tierTypeConfig } from 'config/TierTypeConfig';
import { createAction } from 'utils';
import { syncSourceTranslationToTarget } from './CreateCampaignCouponPageConfig';


const campaignTypes = [
  {
    id: CampaignType.couponsCampaign,
    title: 'Coupon campaign',
    image: CouponCampaignType,
    description:
      'Create coupons and let customers acquire freely or with points',
  },
  {
    id: CampaignType.earningRulesCampaign,
    title: 'Earning campaign',
    image: EarningCampaignType,
    description:
      'Reward customers with points / coupons for fulfilng specific requirements',
  },
]

const earningRuleTypes = [
  {
    id: EarningRuleType.generalPurchase,
    title: 'Purchase',
    image: EarningRuleGerneralPurchase,
    description:
      'Customers can get reward after making purchase',
  },
  {
    id: EarningRuleType.qrCodeScanning,
    title: 'QR code scanning',
    image: EarningRuleQRCode,
    description:
      'Customers can get reward after scanning a specific QR code',
  },
  {
    id: EarningRuleType.gpsCheckIn,
    title: 'GPS check-in',
    image: EarningRuleGPS,
    description: 'Customers can get reward after visiting a specific place and check-in',
  },
  {
    id: EarningRuleType.newMember,
    title: 'New member registration',
    image: EarningRuleNewMember,
    description: 'Give reward to the new member after sign up successfully',
  },
  {
    id: EarningRuleType.memberReferral,
    title: 'Referral',
    image: EarningRuleReferral,
    description: 'Reward referrer (and new member) after the new member signs up successfully',
  },
  {
    id: EarningRuleType.birthday,
    title: 'Birthday',
    image: EarningRuleBirthday,
    description: 'Give reward automatically in customers\' birth months',
  },
  {
    id: EarningRuleType.levelUpgrade,
    title: 'Level upgrade',
    image: EarningRuleLevelUpgrade,
    description: 'Give reward to the member once upgraded',
  },
  {
    id: EarningRuleType.fillingForm,
    title: 'Webhook',
    image: EarningRuleWebhook,
    description: 'Enable earning validation from third party',
  },
];

const displayEarningRuleTypes = earningRuleTypes.filter(item => tierTypeConfig[item.id]);

const isCampaignDisabled = (watch) => {
  const approvalStatus = watch("approvalStatus")
  const campaignPk = watch("pk")
  return !!campaignPk && approvalStatus === ApprovalStatus.published
}

export const CREATE_CAMPAIGN_TYPE_PAGE_CONFIG = {
  extraValidate: ({ getValues, setError, clearErrors }) => true,
  getDisplayComponentKeys: ({ formContext: { watch, getValues } }) => {
    const campaignType = watch('campaignType');
    const rewardType = watch('linkedEarningRules.rewardType');
    const haveCreatedCoupon = watch('haveCreatedCoupon');

    const displayNormalSectionComponents = [
      'campaign_type_key'
    ];

    if (campaignType === CampaignType.earningRulesCampaign) {
      displayNormalSectionComponents.push('reward_type_key', 'earning_rule_type_key');
      if (rewardType === EarningRuleRewardType.coupons) {
        displayNormalSectionComponents.push('have_created_coupon_key');
        if (haveCreatedCoupon) {
          displayNormalSectionComponents.push('coupon_set_dropdown_key')
        }
      }
    } else if (campaignType === CampaignType.couponsCampaign) {
      displayNormalSectionComponents.push('have_created_coupon_key');
      if (haveCreatedCoupon) {
        displayNormalSectionComponents.push('coupon_set_dropdown_key')
      }
    }

    return {
      displayNormalSectionComponents,
      displayLanguageSectionComponents: [],
    };
  },
  normalSections: [
    {
      sectionTitle: '',
      sectionContent: [
        {
          key: 'campaign_type_key',
          complexComponent: ({ formContext: { watch } }) => {
            const disabled = isCampaignDisabled(watch)
            return <TypeSelector
              title="CAMPAIGN TYPE"
              typeConfig={campaignTypes}
              typeFieldName='campaignType'
              customItemClassName='campaign-type-selector'
              itemWidth={510}
              disabled={disabled}
            />
          }
        },
        {
          key: "reward_type_key",
          complexComponent: ({ formContext: { watch }, }) => (
            <ReactHookFormTitleWithRadio
              title="Reward type"
              valueFieldName="linkedEarningRules.rewardType"
              disabled={isCampaignDisabled(watch)}
              options={[
                { label: 'Point', value: EarningRuleRewardType.points },
                { label: 'Coupon', value: EarningRuleRewardType.coupons },
              ]}
            />),
        },
        {
          key: "have_created_coupon_key",
          complexComponent: ({ formContext: { watch, getValues }, }) => (
            <ReactHookFormTitleWithRadio
              title="Have you created the coupon set?"
              valueFieldName="haveCreatedCoupon"
              disabled={isCampaignDisabled(watch)}
              options={[
                { label: 'Yes, select the coupon set below', value: true },
                { label: 'No, create a new coupon set in subsequent steps', value: false },
              ]}
            />),
        },
        {
          key: 'coupon_set_dropdown_key',
          complexComponent: ({
            dispatch,
            formContext: { setValue, watch, getValues },
          }) => {
            const campaignType = watch("campaignType");
            const disabled = isCampaignDisabled(watch)
            return (
              <div className='create-campaign-type-page-linked-coupon'>
                <CouponDropdownWithButtonAndPrompt
                  title=""
                  valueFieldName="linkedCoupon"
                  description=""
                  hasAddButton={false}
                  customOnChange={(coupon) => {
                      dispatch(
                        createAction("createCampaign/getAndLinkCouponDetail")({
                          couponId: coupon?.pk,
                          afterAction: (data) => {
                            if (campaignType !== CampaignType.couponsCampaign) {
                              return
                            }
                            syncSourceTranslationToTarget({
                              getValues,
                              setValue,
                              sourceTranslationData: data?.linkedCoupon?.translations || {},
                              targetTranslationsFieldName: "translations",
                              syncFieldsMap: {
                                name: "generalName",
                                shortDescription: "shortDescription",
                                detailSectionTitle: "detailSectionTitle",
                                detailSectionContent: "detailSectionContent",
                                instructionSectionTitle: "instructionSectionTitle",
                                instructionSectionContent: "instructionSectionContent",
                                coverPhoto: "coverPhoto",
                                detailPhotos: "detailPhotos",
                              }
                            })
                          },
                        }),
                      );
                  }}
                  defaultFilter={{
                    searchField: 'searchNameOrId',
                    isSimpleWithStock: true,
                  }}
                  disableDropDown={disabled}
                />
              </div>
            );
          },
        },
      ],
    },
    {
      sectionTitle: '',
      sectionContent: [
        {
          key: 'earning_rule_type_key',
          complexComponent: ({ formContext: { watch, setValue } }) => (
            <TypeSelector
              title="RULE"
              typeConfig={displayEarningRuleTypes}
              typeFieldName='linkedEarningRules.type'
              customItemClassName='earning-rule-type-selector'
              extraAction={type => {
                if (type === EarningRuleType.birthday) {
                  setValue('isBirthdayRecurringCampaign', true, { shouldDirty: true });
                };
              }}
              disabled={isCampaignDisabled(watch)}
            />
          ),
        },
      ],
    },
  ],
  languageSections: [],
};
